import React, { useEffect, useState } from "react";
import { Row, Col, Typography, Card, Tabs, } from "antd";
import styled from "styled-components";
import { getUserList, getUserInfo, getClientid, getLeaveCategoryList, getRoleList, } from "redux/selectors";
import { bindActionCreators } from "redux";
import { connect, useSelector } from "react-redux";
import queryKeys from "common/queryKey";
import { useFetch } from "hooks/useFetch";
import { CALL_API, CALL_FEATCH_API } from "common/API";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);


const { Title, Text } = Typography;

const Wrapper = styled.div`
  padding: 24px;
`;

const SideMenu = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  .menu-item {
    padding: 10px 14px;
    border-radius: 8px;
    cursor: pointer;
    color: #374151;
    width: 100%;
    :hover {
      background: #e5e7eb;
    }
  }

  .active {
    background: #d1fae5;
    color: #065f46;
    font-weight: 500;
    width: 130%;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const StatCard = styled(Card)`
  background: #f5f5f5;
  border-radius: 12px;
  border: none;

  .ant-card-body {
    max-height: 300px;   /* fixed height */
    overflow-y: auto;    /* scroll inside */
    padding-right: 8px;  /* prevents scrollbar overlap */
  }

  .stat-row {
    display: flex;
    justify-content: space-between;
    padding: 12px 0;
    border-bottom: 1px solid #e5e7eb;

    &:last-child {
      border-bottom: none;
    }
  }

  .label {
    color: #111827;
  }

  .value {
    font-weight: 500;
    color: #4b5563;
  }
`;
const IndividualStat = ({ ClientId, userList, leaveCategoryList, roleList }) => {
  const userInfo = useSelector(getUserInfo);
  const [activeTab, setActiveTab] = useState("general");
  const [leaveData, setLeaveData] = useState([]);

  const filteruser = userList?.filter(
    (item) => item?.userid === userInfo.userid
  );

  const { key: billingDropdownKey, url: billingDropdownUrl } =
    queryKeys.getIndividualGeneral(`?${`userid=${userInfo.userid}`}`);

  const { data: billingDropdownDataRes, refetch: billingDropdownRefetch } = useFetch(
    billingDropdownKey,
    billingDropdownUrl
  );

  const individualStats = billingDropdownDataRes?.response || {};

  const { key: timesheetKey, url: timesheetUrl } =
    queryKeys.getTimeSheetCount(`?${`userid=${userInfo.userid}`}`);

  const { data: timeSheetRes, refetch: timeSheetrefetch } = useFetch(
    timesheetKey,
    timesheetUrl,
    {
      enabled: !!userInfo?.userid   // 👈 only call after userInfo exists
    }
  );

  const timesheet = timeSheetRes?.response?.data;

  useEffect(() => {
    if (!userInfo?.userid) return;

    const fetchLeaveData = async () => {
      const res = await CALL_API(
        `employee-leave-details/${ClientId}`,
        "post",
        {
          userId: userInfo.userid,
          year: new Date().getFullYear().toString(),
          requestType: "mine",
        }
      );

      setLeaveData(res?.response || res || []);
    };

    fetchLeaveData();
  }, [userInfo]);

  const MONTH_MAP = {
    JAN: 0, FEB: 1, MAR: 2, APR: 3, MAY: 4, JUN: 5,
    JUL: 6, AUG: 7, SEP: 8, OCT: 9, NOV: 10, DEC: 11,
  };

  function getDueFriday(weekStr) {
    const startPart = weekStr.split("-")[0];
    const year = parseInt(startPart.slice(0, 4));
    const month = MONTH_MAP[startPart.slice(4, 7)];
    const day = parseInt(startPart.slice(7));
    const monday = new Date(year, month, day);
    const friday = new Date(monday);
    friday.setDate(friday.getDate() + 4);
    return friday;
  }

  /**
   * Parse submitted date string "05-03-2026 12:39:54" → Date
   */
  function parseSubmittedDate(dateStr) {
    if (!dateStr) return null;
    const [datePart, timePart = "00:00:00"] = dateStr.trim().split(" ");
    const [dd, mm, yyyy] = datePart.split("-");
    const [hh, mi, ss] = timePart.split(":");
    return new Date(parseInt(yyyy), parseInt(mm) - 1, parseInt(dd), parseInt(hh), parseInt(mi), parseInt(ss));
  }

  /**
   * Difference in whole days between two dates (a - b), ignoring time
   */
  function diffDays(a, b) {
    const utcA = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
    const utcB = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
    return Math.round((utcA - utcB) / (1000 * 60 * 60 * 24));
  }

  /**
   * Calculate delay for each timesheet entry.
   * - Uses timesheetSubmittedDate if present, else falls back to createdAt
   * - Delay = submittedDate - due Friday of that week (0 if submitted on or before Friday)
   * - Also calculates gap in days from the previous submission
   *
   * @param {Array} data - Array of timesheet objects
   * @returns {Array} - Enriched array with delay info
   */
  function calculateTimesheetDelays(data) {
    // Sort by week start date ascending
    const sorted = [...data].sort((a, b) => {
      return getDueFriday(a.week) - getDueFriday(b.week);
    });

    return sorted.map((item, idx) => {
      const dueFriday = getDueFriday(item.week);

      const submittedDate = item.timesheetSubmittedDate
        ? parseSubmittedDate(item.timesheetSubmittedDate)
        : new Date(item.createdAt);

      const dateUsed = item.timesheetSubmittedDate ? "timesheetSubmittedDate" : "createdAt";

      // Only count delay if submitted AFTER the due Friday
      // If submitted on or before Friday → delayDays = 0
      const rawDiff = diffDays(submittedDate, dueFriday);
      const delayDays = rawDiff > 0 ? rawDiff : 0;

      let gapFromPrevDays = null;
      if (idx > 0) {
        const prev = sorted[idx - 1];
        const prevDate = prev.timesheetSubmittedDate
          ? parseSubmittedDate(prev.timesheetSubmittedDate)
          : new Date(prev.createdAt);
        gapFromPrevDays = diffDays(submittedDate, prevDate);
      }

      return {
        employeeId: item.employeeId,
        employeeName: item.employeeName,
        week: item.week,
        dueFriday: dueFriday.toISOString().split("T")[0],
        submittedDate: submittedDate.toISOString().split("T")[0],
        dateUsed,
        delayDays,                                          // 0 = on time, >0 = days late
        status: delayDays === 0 ? "on_time" : "late",
        gapFromPrevDays,
      };
    });
  }

  /**
   * Calculate summary statistics for a group of processed timesheet rows
   *
   * @param {Array} rows - Output from calculateTimesheetDelays
   * @returns {Object} - Summary stats
   */
  function calculateSummary(rows) {
    const delays = rows.map((r) => r.delayDays);
    const lateDelays = delays.filter((d) => d > 0);

    const avgDelayLateOnly = lateDelays.length
      ? parseFloat((lateDelays.reduce((s, d) => s + d, 0) / lateDelays.length).toFixed(2))
      : 0;

    return {
      avgDelayLateOnly,
    };
  }

  /**
   * Main function — groups by employee, calculates delays and summary per employee
   *
   * @param {Array} data - Raw timesheet array
   * @returns {Object} - { byEmployee: { [employeeId]: { rows, summary } }, overall: summary }
   */
  function processTimesheets(data) {
    const allRows = calculateTimesheetDelays(data);

    // Overall summary across all employees
    const overall = calculateSummary(allRows);

    return { overall };
  }

  const output = processTimesheets(timesheet || []);

  const userGender = filteruser?.[0]?.gender?.toLowerCase();

  const leaveStats = [];

  leaveData?.forEach((item) => {
    const categoryId = Object.keys(item)[0]; // "13", "1", etc
    const categoryArray = item[categoryId];

    if (!Array.isArray(categoryArray)) return;

    const consumed = categoryArray.find((x) => x.type === "consumed");

    const category = leaveCategoryList?.find(
      (c) => String(c.leave_category_id) === String(categoryId)
    );

    if (!category) return;
    const isLocationValid = category?.locationId?.includes(
      filteruser?.[0]?.baselocationid
    );

    if (!isLocationValid) return;

    const eligible = category?.eligible?.toLowerCase();
    if (
      eligible !== "both" &&
      (
        (userGender === "male" && eligible === "female") ||
        (userGender === "female" && eligible === "male")
      )
    ) {
      return;
    }
    leaveStats.push({
      label: `${category.leave_category_name} Leaves Taken`,
      value: consumed?.value || 0,
    });
  });

  const data = [
    {
      label: "Client Survey Average Score",
      value: individualStats.clientSurveyAvgScore ?? 0,
    },
    {
      label: "Engagement Appraisal Average",
      value: individualStats.engagementAppraisalAvgScore ?? 0,
    },

    ...leaveStats,

    {
      label: "Chargeability (target to actual %)",
      value: filteruser[0]?.chargeoutrate,
    },
  ];

  const timesheetData = [
    {
      label: "Avg Timesheet delays in days",
      value: output?.overall?.avgDelayLateOnly || 0,
    },
    {
      label: "Avg Timesheet approval delays in days",
      value: 0,
    },
  ];

  return (
    <Wrapper>
      <Title level={5}>My KPIs</Title>

      <Row gutter={24}>
        {/* Desktop Side Menu */}
        <Col md={5}>
          <SideMenu>
            <div
              className={`menu-item ${activeTab === "general" ? "active" : ""}`}
              onClick={() => setActiveTab("general")}
            >
              General
            </div>
            <div
              className={`menu-item ${activeTab === "timesheet" ? "active" : ""
                }`}
              onClick={() => setActiveTab("timesheet")}
            >
              Timesheet
            </div>
          </SideMenu>
        </Col>

        {/* Mobile Tabs */}
        <Col xs={24} md={0}>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              { key: "general", label: "General" },
              { key: "timesheet", label: "Timesheet" },
            ]}
          />
        </Col>

        {/* Content */}
        <Col xs={24} md={19}>
          <StatCard>
            {(activeTab === "general" ? data : timesheetData)
              .map((item, index) => (
                <div className="stat-row" key={index}>
                  <Text className="label">{item.label}</Text>
                  <Text className="value">{item.value}</Text>
                </div>
              ))}
          </StatCard>
        </Col>
      </Row>
    </Wrapper>
  );
};

const mapStateToProps = (state) => ({
  userList: getUserList(state),
  ClientId: getClientid(state),
  leaveCategoryList: getLeaveCategoryList(state),
  roleList: getRoleList(state),

});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {

    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(IndividualStat);
